'use client';

import { History } from '@repo/types';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@repo/ui/components/ui/accordion';
import { Badge } from '@repo/ui/components/ui/badge';
import { Button } from '@repo/ui/components/ui/button';
import { createFieldResolver } from '@repo/ui/lib/utils/field-label-resolver';
import { Clock, ChevronDown, Download, FileText, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { toast } from 'sonner';
import type { HistoryPdfKind } from './build-history-pdf-snapshot';
import { downloadHistoryPrintPdf } from './download-history-pdf';
import { HistoryTravauxContent } from './history-travaux-content';

interface HistoryItemProps {
  history: History;
  index: number;
  inventaireId: number;
  champignons: any[];
  nuisibles: any[];
  especes: any[];
}

function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return 'Date inconnue';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Date invalide';
  }
}

export function HistoryItem({
  history,
  index,
  inventaireId,
  champignons,
  nuisibles,
  especes,
}: HistoryItemProps) {
  const [loadingKind, setLoadingKind] = useState<HistoryPdfKind | null>(null);

  const resolver = useMemo(() => {
    const tempInventaire = { arbre: history };
    return createFieldResolver(tempInventaire, { champignons, nuisibles, especes });
  }, [history, champignons, nuisibles, especes]);

  const handleDownload = useCallback(
    async (kind: HistoryPdfKind, event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (loadingKind) return;
      setLoadingKind(kind);
      try {
        await downloadHistoryPrintPdf({
          inventaireId,
          history,
          kind,
        });
        toast.success(
          kind === 'fiche_intervention'
            ? 'Fiche intervention (historique) téléchargée'
            : 'Fiche arbre (historique) téléchargée',
        );
      } catch (error) {
        console.error(error);
        toast.error('Impossible de télécharger le PDF historique');
      } finally {
        setLoadingKind(null);
      }
    },
    [history, inventaireId, loadingKind],
  );

  return (
    <AccordionItem
      key={history.id}
      value={`history-${history.id}`}
      className="border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
    >
      <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-gray-50/50 transition-colors duration-200 [&[data-state=open]]:bg-gray-50/50">
        <div className="flex items-center justify-between w-full mr-4 gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <Badge
              variant="outline"
              className="text-xs font-semibold px-2.5 py-1 bg-primary/5 border-primary/20 text-primary shrink-0"
            >
              #{index + 1}
            </Badge>
            <div className="flex items-center gap-2 text-left min-w-0">
              <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-sm text-gray-900 truncate">
                  Modification du {formatDate(history.created_at)}
                </p>
                {history.user_edited_date_travaux && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Travaux prévus le{' '}
                    {new Date(history.user_edited_date_travaux).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0" onClick={(event) => event.stopPropagation()}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              disabled={loadingKind !== null}
              onClick={(event) => handleDownload('fiche_arbre', event)}
              aria-label="Télécharger la fiche arbre historique (même PDF que print)"
            >
              {loadingKind === 'fiche_arbre' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5 mr-1.5" />
              )}
              Fiche arbre
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              disabled={loadingKind !== null}
              onClick={(event) => handleDownload('fiche_intervention', event)}
              aria-label="Télécharger la fiche intervention historique"
            >
              {loadingKind === 'fiche_intervention' ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <FileText className="h-3.5 w-3.5 mr-1.5" />
              )}
              Fiche intervention
            </Button>
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]/trigger:rotate-180" />
          </div>
        </div>
      </AccordionTrigger>

      <AccordionContent className="px-5 pb-5 pt-2 data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
        <HistoryTravauxContent history={history} resolver={resolver} />
      </AccordionContent>
    </AccordionItem>
  );
}
